import React, { useEffect, useMemo, useState } from 'react';
import {
  getArticleOptionGroups, createArticleOptionGroup, updateArticleOptionGroup, deleteArticleOptionGroup,
  getProducts,
} from '../../../lib/api';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogTitle,
} from '../../../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../../components/ui/table';
import {
  SlidersHorizontal, Plus, Pencil, Trash2, Loader2, RefreshCw, Search, X,
  ChevronUp, ChevronDown, Check,
} from 'lucide-react';
import { toast } from 'sonner';

const TABS = [
  { key: 'basic', label: 'Basisdaten' },
  { key: 'input', label: 'Eingabe' },
  { key: 'articles', label: 'Artikelauswahl' },
  { key: 'assignment', label: 'Zuordnung' },
];

function emptyForm() {
  return {
    name: '',
    description: '',
    minEnabled: false,
    min: 1,
    maxEnabled: true,
    max: 1,
    priceMode: 'normal', // free | normal | fixed
    fixedPrice: 0,
    options: [],    // [{ product_id, name, preselected, sort_order }]
    productIds: [], // assignment — products that offer this group
  };
}

// Backend record → edit-form shape
function toForm(group) {
  return {
    name: group.name || '',
    description: group.description || '',
    minEnabled: (group.min_selection || 0) > 0,
    min: group.min_selection || 1,
    maxEnabled: (group.max_selection || 0) > 0,
    max: group.max_selection || 1,
    priceMode: group.price_mode || 'normal',
    fixedPrice: group.fixed_price || 0,
    options: (group.article_options || [])
      .slice()
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
      .map((o) => ({ product_id: o.product_id, name: o.name, preselected: !!o.preselected })),
    productIds: group.product_ids || [],
  };
}

// Edit-form shape → API payload
function toPayload(form) {
  return {
    name: form.name.trim(),
    description: form.description.trim(),
    min_selection: form.minEnabled ? Math.max(0, form.min) : 0,
    max_selection: form.maxEnabled ? Math.max(0, form.max) : 0,
    price_mode: form.priceMode,
    fixed_price: form.priceMode === 'fixed' ? parseFloat(form.fixedPrice) || 0 : 0,
    options: form.options.map((o, index) => ({
      product_id: o.product_id,
      name: o.name,
      preselected: o.preselected,
      sort_order: index,
    })),
    product_ids: form.productIds,
  };
}

function ProductAutocomplete({ products, excludeIds, onAdd, placeholder }) {
  const [query, setQuery] = useState('');
  const excluded = useMemo(() => new Set(excludeIds), [excludeIds]);

  const matches = useMemo(() => {
    if (!query.trim()) return [];
    const q = query.trim().toLowerCase();
    return products.filter((p) => !excluded.has(p.id) && p.name.toLowerCase().includes(q)).slice(0, 8);
  }, [products, excluded, query]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && matches.length > 0) {
      e.preventDefault();
      onAdd(matches[0]);
      setQuery('');
    }
  };

  return (
    <div className="relative">
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
      <Input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className="pl-9"
      />
      {matches.length > 0 && (
        <div className="absolute z-10 mt-1 w-full max-h-56 overflow-y-auto rounded-md border border-slate-200 bg-white shadow-lg">
          {matches.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => { onAdd(p); setQuery(''); }}
              className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-slate-50"
            >
              <span>{p.name}</span>
              <span className="text-xs text-slate-400 tabular-nums">€{Number(p.price || 0).toFixed(2)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminArticleOptions({ embedded = false }) {
  const [groups, setGroups] = useState([]);
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const [showDialog, setShowDialog] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [activeTab, setActiveTab] = useState('basic');
  const [form, setForm] = useState(emptyForm());

  useEffect(() => { load(); }, []);

  const load = async () => {
    setIsLoading(true);
    try {
      const [groupsData, productsData] = await Promise.all([getArticleOptionGroups(), getProducts()]);
      setGroups(groupsData);
      setProducts(productsData);
    } catch {
      toast.error('Fehler beim Laden');
    } finally {
      setIsLoading(false);
    }
  };

  const filteredGroups = groups.filter((g) => g.name.toLowerCase().includes(searchQuery.toLowerCase()));
  const productById = (id) => products.find((p) => p.id === id);

  const openDialog = (group = null) => {
    setEditingGroup(group);
    setForm(group ? toForm(group) : emptyForm());
    setActiveTab('basic');
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Bitte Namen eingeben'); setActiveTab('basic'); return; }
    if (form.options.length === 0) { toast.error('Bitte mindestens einen Artikel auswählen'); setActiveTab('articles'); return; }

    setIsSaving(true);
    try {
      const payload = toPayload(form);
      if (editingGroup) {
        await updateArticleOptionGroup(editingGroup.id, payload);
        toast.success('Optionsgruppe aktualisiert');
      } else {
        await createArticleOptionGroup(payload);
        toast.success('Optionsgruppe erstellt');
      }
      setShowDialog(false);
      load();
    } catch {
      toast.error('Fehler beim Speichern');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (group) => {
    if (!window.confirm(`Optionsgruppe "${group.name}" wirklich löschen?`)) return;
    try {
      await deleteArticleOptionGroup(group.id);
      toast.success('Optionsgruppe gelöscht');
      setGroups((gs) => gs.filter((g) => g.id !== group.id));
    } catch {
      toast.error('Fehler beim Löschen');
    }
  };

  // ── Article selection (the option choices themselves) ──────────────────────
  const addOptionArticle = (product) => {
    setForm((f) => ({
      ...f,
      options: [...f.options, { product_id: product.id, name: product.name, preselected: false }],
    }));
  };

  const removeOptionArticle = (productId) => {
    setForm((f) => ({ ...f, options: f.options.filter((o) => o.product_id !== productId) }));
  };

  const togglePreselected = (productId) => {
    setForm((f) => ({
      ...f,
      options: f.options.map((o) => (o.product_id === productId ? { ...o, preselected: !o.preselected } : o)),
    }));
  };

  const moveOption = (productId, direction) => {
    setForm((f) => {
      const options = [...f.options];
      const from = options.findIndex((o) => o.product_id === productId);
      const to = from + direction;
      if (from < 0 || to < 0 || to >= options.length) return f;
      [options[from], options[to]] = [options[to], options[from]];
      return { ...f, options };
    });
  };

  // ── Assignment (which products offer this group) ────────────────────────────
  const addAssignedProduct = (product) => {
    setForm((f) => ({ ...f, productIds: [...f.productIds, product.id] }));
  };

  const removeAssignedProduct = (productId) => {
    setForm((f) => ({ ...f, productIds: f.productIds.filter((id) => id !== productId) }));
  };

  if (isLoading) {
    return (
      <div className={`flex items-center justify-center ${embedded ? 'min-h-[220px]' : 'h-screen'}`}>
        <Loader2 className="w-8 h-8 animate-spin text-[#1a2f38]" />
      </div>
    );
  }

  return (
    <div className={`${embedded ? 'space-y-6' : 'space-y-6 p-6'}`}>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1a2f38]">Artikeloptionen</h1>
          <p className="text-slate-500">{groups.length} Optionsgruppen</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={load} disabled={isLoading}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Aktualisieren
          </Button>
          <Button className="bg-[#1a2f38] hover:bg-[#2a4048]" onClick={() => openDialog()}>
            <Plus className="w-4 h-4 mr-2" />
            Neue Artikeloption
          </Button>
        </div>
      </div>

      <div className="relative max-w-md">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="In Artikeloptionen suchen" className="pl-9" />
      </div>

      <Card className="shadow-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Bezeichnung</TableHead>
                <TableHead className="text-center">Artikel</TableHead>
                <TableHead className="text-center">Auswahl</TableHead>
                <TableHead className="w-[100px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredGroups.map((group) => (
                <TableRow key={group.id} className="cursor-pointer" onClick={() => openDialog(group)}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-violet-50 flex items-center justify-center shrink-0">
                        <SlidersHorizontal className="w-4 h-4 text-violet-600" />
                      </div>
                      <span className="font-medium">{group.name}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-center text-slate-500">{(group.article_options || []).length}</TableCell>
                  <TableCell className="text-center text-slate-500">
                    {(group.min_selection || 0)}–{(group.max_selection || 0) || '∞'}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); openDialog(group); }}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost" size="icon"
                        className="text-red-500 hover:text-red-600 hover:bg-red-50"
                        onClick={(e) => { e.stopPropagation(); handleDelete(group); }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {filteredGroups.length === 0 && (
            <div className="text-center py-16">
              <SlidersHorizontal className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p className="text-slate-500">Noch keine Artikeloptionen</p>
              <Button className="mt-4 bg-[#1a2f38]" onClick={() => openDialog()}>
                Erste Artikeloption erstellen
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modify article option — tabbed dialog (diekassa layout) */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-4xl p-0 gap-0 overflow-hidden [&>button]:hidden">
          <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{form.name || 'Neu'}</p>
              <DialogTitle className="text-lg font-bold text-slate-900">Artikeloption bearbeiten</DialogTitle>
            </div>
            <button onClick={() => setShowDialog(false)} className="text-slate-400 hover:text-slate-700">
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="grid grid-cols-[180px_1fr] min-h-[420px]">
            <div className="border-r border-slate-200 p-3 space-y-1">
              {TABS.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveTab(tab.key)}
                  className={`w-full rounded-lg px-3 py-2 text-left text-sm font-medium transition-colors ${
                    activeTab === tab.key ? 'bg-emerald-50 text-emerald-800' : 'text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="p-6 overflow-y-auto max-h-[70vh]">
              {activeTab === 'basic' && (
                <div className="space-y-4 max-w-lg">
                  <div>
                    <Label>Name *</Label>
                    <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="z.B. Extras, Wähle deine Pastasauce" autoFocus />
                  </div>
                  <div>
                    <Label>Beschreibung</Label>
                    <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Optional" />
                  </div>
                </div>
              )}

              {activeTab === 'input' && (
                <div className="grid grid-cols-2 gap-6 max-w-2xl">
                  <div className="rounded-xl bg-slate-50 p-4 space-y-3">
                    <p className="font-semibold text-slate-800">Auswahl</p>
                    <label className="flex items-center gap-2">
                      <input type="checkbox" checked={form.minEnabled} onChange={(e) => setForm({ ...form, minEnabled: e.target.checked })} />
                      <span className="text-sm">Min.</span>
                      <Input
                        type="number" min={0} disabled={!form.minEnabled} value={form.min}
                        onChange={(e) => setForm({ ...form, min: parseInt(e.target.value) || 0 })}
                        className="w-20"
                      />
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="checkbox" checked={form.maxEnabled} onChange={(e) => setForm({ ...form, maxEnabled: e.target.checked })} />
                      <span className="text-sm">Max.</span>
                      <Input
                        type="number" min={0} disabled={!form.maxEnabled} value={form.max}
                        onChange={(e) => setForm({ ...form, max: parseInt(e.target.value) || 0 })}
                        className="w-20"
                      />
                    </label>
                    <p className="text-xs text-slate-400">Ohne Haken = kein Limit (Min 0 / Max unbegrenzt).</p>
                  </div>
                  <div className="rounded-xl bg-slate-50 p-4 space-y-3">
                    <p className="font-semibold text-slate-800">Preis</p>
                    {[
                      { value: 'free', label: 'Kostenlos' },
                      { value: 'normal', label: 'Normaler Artikelpreis' },
                      { value: 'fixed', label: 'Fester Aufpreis' },
                    ].map((opt) => (
                      <label key={opt.value} className="flex items-center gap-2">
                        <input type="radio" name="priceMode" checked={form.priceMode === opt.value} onChange={() => setForm({ ...form, priceMode: opt.value })} />
                        <span className="text-sm">{opt.label}</span>
                      </label>
                    ))}
                    {form.priceMode === 'fixed' && (
                      <Input
                        type="number" min={0} step={0.1} value={form.fixedPrice}
                        onChange={(e) => setForm({ ...form, fixedPrice: e.target.value })}
                        className="w-28"
                      />
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'articles' && (
                <div className="max-w-xl space-y-3">
                  <ProductAutocomplete
                    products={products}
                    excludeIds={form.options.map((o) => o.product_id)}
                    onAdd={addOptionArticle}
                    placeholder="Artikel suchen und mit ENTER hinzufügen"
                  />
                  <div className="rounded-lg border border-slate-200">
                    <div className="flex items-center gap-2 border-b border-slate-100 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
                      <span className="w-14">Vorauswahl</span>
                      <span className="flex-1">Artikel</span>
                    </div>
                    {form.options.length === 0 ? (
                      <p className="px-3 py-6 text-center text-sm text-slate-400">Noch keine Artikel ausgewählt</p>
                    ) : (
                      form.options.map((option, index) => (
                        <div key={option.product_id} className="flex items-center gap-2 border-b border-slate-100 px-3 py-2 last:border-b-0">
                          <div className="flex w-14 flex-col">
                            <button type="button" onClick={() => moveOption(option.product_id, -1)} disabled={index === 0} className="text-slate-400 hover:text-slate-700 disabled:opacity-30">
                              <ChevronUp className="h-3.5 w-3.5" />
                            </button>
                            <button type="button" onClick={() => moveOption(option.product_id, 1)} disabled={index === form.options.length - 1} className="text-slate-400 hover:text-slate-700 disabled:opacity-30">
                              <ChevronDown className="h-3.5 w-3.5" />
                            </button>
                          </div>
                          <button
                            type="button"
                            onClick={() => togglePreselected(option.product_id)}
                            className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border ${option.preselected ? 'border-emerald-500 bg-emerald-500 text-white' : 'border-slate-300'}`}
                          >
                            {option.preselected ? <Check className="h-3.5 w-3.5" /> : null}
                          </button>
                          <span className="flex-1 text-sm font-medium text-slate-800">{option.name}</span>
                          <span className="text-xs text-slate-400 tabular-nums">
                            {form.priceMode === 'free' ? 'kostenlos' : form.priceMode === 'fixed' ? `+€${(parseFloat(form.fixedPrice) || 0).toFixed(2)}` : `€${Number(productById(option.product_id)?.price || 0).toFixed(2)}`}
                          </span>
                          <button type="button" onClick={() => removeOptionArticle(option.product_id)} className="text-slate-400 hover:text-red-500">
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'assignment' && (
                <div className="max-w-xl space-y-3">
                  <p className="text-sm text-slate-500">Diese Artikeloption wird beim Bonieren für folgende Artikel angeboten.</p>
                  <ProductAutocomplete
                    products={products}
                    excludeIds={form.productIds}
                    onAdd={addAssignedProduct}
                    placeholder="Artikel suchen und mit ENTER zuordnen"
                  />
                  <div className="rounded-lg border border-slate-200">
                    {form.productIds.length === 0 ? (
                      <p className="px-3 py-6 text-center text-sm text-slate-400">Noch keinem Artikel zugeordnet</p>
                    ) : (
                      form.productIds.map((productId) => (
                        <div key={productId} className="flex items-center gap-2 border-b border-slate-100 px-3 py-2 last:border-b-0">
                          <span className="flex-1 text-sm font-medium text-slate-800">{productById(productId)?.name || productId}</span>
                          <button type="button" onClick={() => removeAssignedProduct(productId)} className="text-slate-400 hover:text-red-500">
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 border-t border-slate-200 px-6 py-4">
            <button onClick={() => setShowDialog(false)} className="text-sm font-semibold text-slate-600 hover:text-slate-900">
              Abbrechen
            </button>
            <Button onClick={handleSave} disabled={isSaving} className="bg-emerald-500 hover:bg-emerald-600">
              {isSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Speichern
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
